import React, { useEffect, useState } from 'react';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile } from '../../types/chemistry';

interface ProjectScreensTabProps {
  projectId: number;
}

const CHEM_EXT = new Set(['sdf','mol','mol2','smi','smiles','csv','xlsx','xls','cdx','cdxml','cml','inchi','rxn','rdf']);

const isChemFile = (f: ChemFile) => {
  if (f.fileType) return f.fileType === 'molecules';
  // No fileType set — fall back to extension check (backward compat for older files)
  // Check originalFileName first, then fileName (originalFileName may lack extension after rename)
  const origExt = (f.originalFileName || '').split('.').pop()?.toLowerCase();
  if (origExt && CHEM_EXT.has(origExt)) return true;
  const ext = (f.fileName || '').split('.').pop()?.toLowerCase();
  return !!ext && CHEM_EXT.has(ext);
};

const ProjectScreensTab: React.FC<ProjectScreensTabProps> = ({ projectId }) => {
  const [files, setFiles] = useState<ChemFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const screenFiles = await chemistryApi.getFiles(projectId, 'screen');
        setFiles(screenFiles.filter(isChemFile));
      } catch { /* ignore */ }
      finally { setIsLoading(false); }
    })();
  }, [projectId]);

  const totalMolecules = files.reduce((sum, f) => sum + f.moleculesLibraryCount, 0);

  // Detect files that look like screening data (have IC50, EC50, Ki, Kd, % Inhibition columns)
  const screeningColumns = ['IC50', 'EC50', 'Ki', 'Kd', 'pIC50', 'pEC50', '% Inhibition', 'Percent Inhibition', 'Activity'];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <span className="text-gray-500 dark:text-gray-400 font-medium">Loading screening data...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">Screening Data</h2>
          <p className="text-sm text-gray-500 mt-1">
            Biological assay results and dose-response data
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-violet-100 to-purple-50 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
          {totalMolecules.toLocaleString()} compounds across {files.length} files
        </p>
        <p className="text-gray-400 mt-2 max-w-lg mx-auto">
          Screening data is automatically detected from files containing activity columns
          (IC50, EC50, Ki, % Inhibition). Upload assay data files to populate this view.
        </p>

        {/* Show detected screening columns if any files have them */}
        <div className="mt-8 flex flex-wrap gap-2 justify-center">
          {screeningColumns.map(col => (
            <span key={col} className="px-3 py-1.5 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 text-xs font-medium rounded-full">
              {col}
            </span>
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-6">
          Tip: Files with columns named IC50, EC50, Ki, or similar will appear here automatically.
        </p>
      </div>
    </div>
  );
};

export default ProjectScreensTab;
